import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Autocomplete,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {
  useApplyComplexFilterMutation,
  useGetCurrentUserFilterQuery,
} from '../../app/services/complexFilters';
import { useGetGroupsQuery } from '../../app/services/groups';
import { useGetMachinesQuery } from '../../app/services/machines';
import { useGetLocationsQuery } from '../../app/services/locations';
import { useGetMaintenanceTypesQuery } from '../../app/services/maintenanceTypes';

const ConditionRow = React.memo(
  ({
    condition,
    index,
    onOperatorChange,
    onFieldTypeChange,
    onValuesChange,
    onDelete,
    fieldTypes,
    usedFieldTypes,
    optionsMap,
    t,
  }) => {
    const options = optionsMap[condition.fieldType] || [];

    return (
      <Box>
        {index > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
            <ToggleButtonGroup
              value={condition.operator}
              exclusive
              onChange={(_e, value) => {
                if (value !== null) onOperatorChange(condition.id, value);
              }}
              size="small"
            >
              <ToggleButton value="AND">{t('complexFilters.and')}</ToggleButton>
              <ToggleButton value="OR">{t('complexFilters.or')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <FormControl sx={{ minWidth: { xs: '100%', sm: 180 }, flexShrink: 0 }}>
            <InputLabel>{t('complexFilters.field')}</InputLabel>
            <Select
              value={condition.fieldType}
              onChange={(e) => onFieldTypeChange(condition.id, e.target.value)}
              label={t('complexFilters.field')}
            >
              {fieldTypes.map((ft) => {
                const isUsedElsewhere =
                  usedFieldTypes.has(ft.value) && condition.fieldType !== ft.value;
                return (
                  <MenuItem key={ft.value} value={ft.value} disabled={isUsedElsewhere}>
                    {ft.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flex: 1, gap: 1, alignItems: 'center', minWidth: 0 }}>
            <Autocomplete
              multiple
              sx={{ flex: 1, minWidth: 0 }}
              options={options}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              value={condition.selectedValues}
              onChange={(_event, newValue) => onValuesChange(condition.id, newValue)}
              disabled={!condition.fieldType}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('complexFilters.value')}
                  placeholder={t('complexFilters.searchPlaceholder')}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, tagIndex) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index: tagIndex })}
                    key={option.id}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <IconButton
              color="error"
              onClick={() => onDelete(condition.id)}
              sx={{ flexShrink: 0 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }
);

ConditionRow.displayName = 'ConditionRow';

const CalendarFilterBuilder = ({ onFilterChange }) => {
  const { t } = useTranslation();
  const [conditions, setConditions] = useState([]);
  const debounceRef = useRef(null);
  const justLoadedFilterRef = useRef(false);
  const initialApplyDoneRef = useRef(false);

  const [applyFilter, { isLoading: isApplying }] = useApplyComplexFilterMutation();
  const { data: savedFilter, isLoading: isLoadingFilter } = useGetCurrentUserFilterQuery();

  const { data: machineGroupsData } = useGetGroupsQuery();
  const { data: machinesData } = useGetMachinesQuery();
  const { data: locationsData } = useGetLocationsQuery();
  const { data: maintenanceTypesData } = useGetMaintenanceTypesQuery();

  const machineGroups = useMemo(() => {
    if (!machineGroupsData) return [];
    return [
      ...(machineGroupsData.activeMachineGroups || []),
      ...(machineGroupsData.archivedMachineGroups || []),
    ].map((g) => ({ id: parseInt(g.id), name: g.name }));
  }, [machineGroupsData]);

  const machines = useMemo(() => {
    if (!machinesData) return [];
    return [
      ...(machinesData.activeMachines || []),
      ...(machinesData.archivedMachines || []),
    ].map((m) => ({ id: parseInt(m.id), name: `${m.name} ${m.serial_number}` }));
  }, [machinesData]);

  const locations = useMemo(() => {
    if (!Array.isArray(locationsData)) return [];
    return locationsData.map((l) => ({ id: parseInt(l.id), name: l.name }));
  }, [locationsData]);

  const maintenanceTypes = useMemo(() => {
    if (!Array.isArray(maintenanceTypesData)) return [];
    return maintenanceTypesData.map((mt) => ({ id: parseInt(mt.id), name: mt.name }));
  }, [maintenanceTypesData]);

  const optionsMap = useMemo(
    () => ({ machineGroups, machines, locations, maintenanceTypes }),
    [machineGroups, machines, locations, maintenanceTypes]
  );

  const fieldTypes = useMemo(
    () => [
      { value: 'machineGroups', label: t('complexFilters.machineGroups') },
      { value: 'machines', label: t('complexFilters.machines') },
      { value: 'locations', label: t('complexFilters.locations') },
      { value: 'maintenanceTypes', label: t('complexFilters.maintenanceTypes') },
    ],
    [t]
  );

  const usedFieldTypes = useMemo(() => {
    const set = new Set();
    conditions.forEach((c) => { if (c.fieldType) set.add(c.fieldType); });
    return set;
  }, [conditions]);

  // Auto-load saved filter on mount (once all data is ready)
  useEffect(() => {
    if (
      savedFilter &&
      savedFilter.filterStructure &&
      !isLoadingFilter &&
      machineGroups.length > 0 &&
      machines.length > 0 &&
      !initialApplyDoneRef.current
    ) {
      initialApplyDoneRef.current = true;

      const optionMaps = {
        machineGroups: new Map(machineGroups.map((g) => [g.id, g])),
        machines: new Map(machines.map((m) => [m.id, m])),
        locations: new Map(locations.map((l) => [l.id, l])),
        maintenanceTypes: new Map(maintenanceTypes.map((mt) => [mt.id, mt])),
      };

      const loadedConditions = savedFilter.filterStructure.map((c, index) => {
        const optionMap = optionMaps[c.fieldType];
        const selectedValues = c.selectedValues.map((id) => optionMap?.get(id)).filter(Boolean);
        return { id: Date.now() + index, fieldType: c.fieldType, operator: c.operator, selectedValues };
      });

      if (loadedConditions.length > 0) {
        justLoadedFilterRef.current = true;
        setConditions(loadedConditions);
      }
    }
  }, [savedFilter, isLoadingFilter, machineGroups, machines, locations, maintenanceTypes]);

  // Auto-apply after loading from DB
  useEffect(() => {
    if (justLoadedFilterRef.current && conditions.length > 0) {
      const validConditions = conditions.filter((c) => c.fieldType && c.selectedValues.length > 0);
      if (validConditions.length > 0) {
        const conditionsDto = validConditions.map((c) => ({
          fieldType: c.fieldType,
          operator: c.operator,
          selectedValues: c.selectedValues.map((v) => v.id),
        }));
        applyFilter(conditionsDto)
          .unwrap()
          .then((results) => {
            onFilterChange(results.machines.map((m) => parseInt(m.id)));
            justLoadedFilterRef.current = false;
          })
          .catch(() => {
            justLoadedFilterRef.current = false;
          });
      } else {
        justLoadedFilterRef.current = false;
      }
    }
  }, [conditions, applyFilter, onFilterChange]);

  // Debounced auto-apply on condition changes (not on initial load)
  const applyDebounced = useCallback(
    (newConditions) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const validConditions = newConditions.filter(
          (c) => c.fieldType && c.selectedValues.length > 0
        );
        if (validConditions.length === 0) {
          onFilterChange(null);
          return;
        }
        const conditionsDto = validConditions.map((c) => ({
          fieldType: c.fieldType,
          operator: c.operator,
          selectedValues: c.selectedValues.map((v) => v.id),
        }));
        applyFilter(conditionsDto)
          .unwrap()
          .then((results) => {
            onFilterChange(results.machines.map((m) => parseInt(m.id)));
          })
          .catch(() => {});
      }, 500);
    },
    [applyFilter, onFilterChange]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleAddCondition = useCallback(() => {
    setConditions((prev) => {
      const next = [...prev, { id: Date.now(), fieldType: '', operator: 'AND', selectedValues: [] }];
      if (!justLoadedFilterRef.current) applyDebounced(next);
      return next;
    });
  }, [applyDebounced]);

  const handleDeleteCondition = useCallback(
    (id) => {
      setConditions((prev) => {
        const next = prev.filter((c) => c.id !== id);
        if (!justLoadedFilterRef.current) applyDebounced(next);
        return next;
      });
    },
    [applyDebounced]
  );

  const handleFieldTypeChange = useCallback(
    (id, newFieldType) => {
      setConditions((prev) => {
        const next = prev.map((c) =>
          c.id === id ? { ...c, fieldType: newFieldType, selectedValues: [] } : c
        );
        if (!justLoadedFilterRef.current) applyDebounced(next);
        return next;
      });
    },
    [applyDebounced]
  );

  const handleOperatorChange = useCallback(
    (id, newOperator) => {
      setConditions((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, operator: newOperator } : c));
        if (!justLoadedFilterRef.current) applyDebounced(next);
        return next;
      });
    },
    [applyDebounced]
  );

  const handleValuesChange = useCallback(
    (id, newValues) => {
      setConditions((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, selectedValues: newValues } : c));
        if (!justLoadedFilterRef.current) applyDebounced(next);
        return next;
      });
    },
    [applyDebounced]
  );

  const handleClear = useCallback(() => {
    setConditions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onFilterChange(null);
  }, [onFilterChange]);

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {t('complexFilters.title')}
        </Typography>
        {isApplying && <CircularProgress size={16} />}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddCondition}
        >
          {t('complexFilters.addCondition')}
        </Button>
        {conditions.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={handleClear}
          >
            {t('complexFilters.clear')}
          </Button>
        )}
      </Box>

      {conditions.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {conditions.map((condition, index) => (
            <ConditionRow
              key={condition.id}
              condition={condition}
              index={index}
              onOperatorChange={handleOperatorChange}
              onFieldTypeChange={handleFieldTypeChange}
              onValuesChange={handleValuesChange}
              onDelete={handleDeleteCondition}
              fieldTypes={fieldTypes}
              usedFieldTypes={usedFieldTypes}
              optionsMap={optionsMap}
              t={t}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CalendarFilterBuilder;
